import React ,{ useCallback, useEffect, useState } from "react";
import styles from './StudentForm.module.scss';
import PropTypes from "prop-types";
import ImageUploader from "../imageUploader/ImageUploader";
import Dropdown from "../dropdown/Dropdown";
import TextField from "../textField/TextField";
import Button from "../button/Button";
import { useNavigate, useParams } from "react-router-dom";
import CustomDatePicker from "../customDatePicker/CustomDatePicker";
import MultiSelectDropdown from "../multiSelectDropdown/MultiSelectDropdown";
import { formatPhoneNumber, getInitials } from "../../utils/stringUtils";
import { useToasts } from "../../contexts/ToastContext";

const StudentForm = ({
    formData,
    specialityList = [],
    yearData=[],
    instructorList=[],
    onDataChange,
    onSubmit
}) => {
  const [profilePicture, setProfilePicture] = useState({
    initials: '',
    image: null
  });
  const [imageFile, setImageFile] = useState(null);
  const [firstNameError, setFirstNameError] = useState('');
  const [lastNameError, setLastNameError] = useState('');
  const [emailError, setEmailError] = useState('');
  const [phoneError, setPhoneError] = useState('');
  const [studentIdError, setStudentIdError] = useState('');
  const [dobError, setDobError] = useState('');
  const [yearError, setYearError] = useState('');
  const [semesterError, setSemesterError] = useState('');
  const [monthError, setMonthError] = useState('');
  const [enrollmentDateError, setEnrollmentDateError] = useState('');
  const [graduationDateError, setGraduationDateError] = useState('');
  const [isValidated, setIsValidated] = useState(false);

  const { studentId } = useParams();
  const { showToast } = useToasts();
  const navigate = useNavigate();

  useEffect(() => {
    setProfilePicture({
      initials: getInitials(formData?.firstName, formData?.lastName),
      image: formData?.profilePicture || null
    });
  }, [formData]);

  const validateForm = useCallback(() => {
    const validateFirstName = () => {
      if (!formData.firstName) {
        return 'First Name is required';
      }
      if (!/^[A-Za-z]{2,}/.test(formData.firstName)) {
        return 'First Name must contain only letters and be at least 2 characters long.';
      }
      return '';
    };

    const validateLastName = () => {
      if (!formData.lastName) {
        return 'Last Name is required';
      }
      if (!/^[A-Za-z]{2,}/.test(formData.lastName)) {
        return 'Last Name must contain only letters and be at least 2 characters long.';
      }
      return '';
    };

    const validateEmail = () => {
      if (!formData.email) {
        return 'Email is required';
      }
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
        return 'Invalid email format';
      }
      return '';
    };

    const validatePhone = () => {
      const cleanedValue = formData.mobileNumber.replace(/\D/g, '');
      if (!cleanedValue) {
        return 'Phone number is required';
      }
      if (!/^\d{10}$/.test(cleanedValue)) {
        return 'Invalid phone number format. Must be 10 digits.';
      }
      return '';
    };

    const validateStudentId = () => {
      if (!formData.studentId) {
        return 'StudentId is required';
      }
      return '';
    };

    const validateDob = () => {
      if (!formData.dob) {
        return 'Date of Birth is required';
      }
      return '';
    };

    const validateEnrollmentDate = () => {
      if (!formData.enrollmentDate) {
        return 'Enrollment Date is required';
      }
      return '';
    };

    const validateGraduationDate = () => {
      if (!formData.graduationDate) {
        return 'Graduation Date is required';
      }
      return '';
    };

    const validateYear = () => {
      if (!formData.year) {
        return 'Program Year is required';
      }
      return '';
    };

    const validateSemester = () => {
      if (!formData.semester) {
        return 'Program Semester is required';
      }
      return '';
    };

    const validateMonth = () => {
      if (!formData.month) {
        return 'Program Month is required';
      }
      return '';
    };

    setIsValidated(true);
    const firstNameValidationResult = validateFirstName();
    const lastNameValidationResult = validateLastName();
    const emailValidationResult = validateEmail();
    const phoneValidationResult = validatePhone();
    const studentIdValidationResult= validateStudentId();
    const dobValidationResult = validateDob();
    const enrollmentValidationResult=validateEnrollmentDate();
    const graduationValidationResult=validateGraduationDate();
    const yearValidationResult=validateYear();
    const semesterValidationResult=validateSemester()
    const monthValidationResult=validateMonth()


    setFirstNameError(firstNameValidationResult);
    setLastNameError(lastNameValidationResult);
    setEmailError(emailValidationResult);
    setPhoneError(phoneValidationResult);
    setStudentIdError(studentIdValidationResult);
    setDobError(dobValidationResult);
    setEnrollmentDateError(enrollmentValidationResult);
    setGraduationDateError(graduationValidationResult);
    setYearError(yearValidationResult);
    setSemesterError(semesterValidationResult);
    setMonthError(monthValidationResult);

    const errors = [
      { field: 'firstName', message: firstNameValidationResult },
      { field: 'lastName', message: lastNameValidationResult },
      { field: 'email', message: emailValidationResult },
      { field: 'phone', message: phoneValidationResult },
      { field: 'studentid', message: studentIdValidationResult },
      { field: 'dateofbirth', message: dobValidationResult },
      { field: 'enrollmentdate', message: enrollmentValidationResult },
      { field: 'graduationdate', message: graduationValidationResult },
      { field: 'year', message: yearValidationResult },
      { field: 'semester', message: semesterValidationResult },
      { field: 'month', message: monthValidationResult },
    ];

    const firstError = errors.find(error => error.message !== '');
    return firstError ? firstError.field : null; // Return the field name of the first error
  }, [formData]);

  const handleSubmit = () => {
    const firstErrorField = validateForm();
    if (firstErrorField) {
      scrollToFirstError(firstErrorField);
      return false;
    }
    onSubmit(imageFile);
  };

  const scrollToFirstError = (field) => {
    const headerHeight = 72; // Your fixed header height
    const element = document.querySelector(`[data-error-field="${field}"]`);
    if (element) {
      const elementTop = element.offsetTop - headerHeight; // Calculate adjusted position

      window.scrollTo({
        top: elementTop,
        behavior: 'smooth',
      });
    }
  };

  useEffect(() => {
    if (isValidated) {
      validateForm();
    }
  }, [formData, isValidated, validateForm]);

  const getSemesters = (year) => {
    if(year) {
      const selectedYearData = yearData.find(item => item.id === parseInt(year));
      if(selectedYearData?.semesters) {
        const semesters = [];
        selectedYearData.semesters.forEach((item) => {
            semesters.push(item);
        });
        return semesters;
      }
    }
    return [];
  }

  const getMonths = (year,semester) => {
    if(year && semester) {
      const selectedYearData = yearData.find(item => item.id === parseInt(year));
      if(selectedYearData?.semesters) {
        const selectedSemesterData=selectedYearData.semesters.find(item => item.id === parseInt(semester))
        if(selectedSemesterData?.months) {
          const months = [];
          selectedSemesterData.months.forEach((item) => {
              months.push(item);
          });
          return months;
        }   
      }
    }
    return [];
  }

  return (
    <form
      className={styles.form}
      onSubmit={(e) => {
        e.preventDefault();
        handleSubmit();
      }}
    >
      <h2>Personal Information</h2>
      <ImageUploader
        {...profilePicture}
        onChange={(value) => {
          setImageFile(value);
        }}
      />
      <div className={styles.row}>
        <div className={styles.column50} data-error-field="firstName">
          <TextField
            label="First Name"
            value={formData.firstName}
            onChange={(value) => {
              onDataChange({
                firstName: value
              });
            }}
            error={firstNameError}
          />
        </div>
        <div className={styles.column50} data-error-field="lastName">
          <TextField
            label="Last Name"
            value={formData.lastName}
            onChange={(value) => {
              onDataChange({
                lastName: value
              });
            }}
            error={lastNameError}
          />
        </div>
      </div>
      <div className={styles.row}>
        <div className={styles.column50} data-error-field="email">
          <TextField
            label="Email Address"
            type="email"
            value={formData.email}
            onChange={(value) => {
              onDataChange({
                email: value
              });
            }}
            error={emailError}
          />
        </div>
        <div className={styles.column30} data-error-field="dateofbirth">
          <CustomDatePicker
            label="Date of Birth"
            date={formData.dob}
            onChange={(value) => {
            onDataChange({
              dob: value
            });
            }}
            error={dobError}
          />
        </div>  
      </div>
      <div className={styles.row}>
        <div className={styles.column50} data-error-field="phone">
          <TextField
            label="Phone Number"
            value={formData.mobileNumber}
            onChange={(value) => {
              onDataChange({
                mobileNumber: formatPhoneNumber(value)
              });
            }}
            error={phoneError}
          />
        </div>
        <div className={styles.column50} data-error-field="studentid">
          <TextField
            label="Student ID"
            value={formData.studentId}
            onChange={(value) => {
              onDataChange({
                studentId: value
              });
            }}
            error={studentIdError}
          />
        </div>
      </div>
      <h2>Academic Information</h2>
      <div className={styles.row}>
        <div className={styles.column30} data-error-field="year">
          <Dropdown
            label="Year"
            options={yearData.map((year) => ({
                value: year.id,
                label: year.year,
              }))}
            value={formData.year}
            onChange={(selected) => {
              onDataChange({
                year: selected,
                semester:"",
                month:""
              });
            }}
            error={yearError}
          />
        </div>
        <div className={styles.column30} data-error-field="semester">
          <Dropdown
            label="Semester"
            options={
              getSemesters(formData.year).map((semester) => ({
              value: semester.id,
              label: semester.semester,
            }))
            }
            value={formData.semester}
            onChange={
            (selected) => {
              onDataChange({
                semester: selected,
                month:""
              });
            }}
            error={semesterError}
          />
        </div>
        <div className={styles.column30} data-error-field="month">
          <Dropdown
            label="Month"
            options={
              getMonths(formData.year,formData.semester).map((month) => ({
              value: month.id,
              label: month.month,
            }))
            }
            value={formData.month}
            onChange={(selected) => {
              onDataChange({
                month: selected
              });
            }}
            error={monthError}
          />
        </div>
      </div>
      <div className={styles.row}>
        <div className={styles.column20} data-error-field="enrollmentdate">
          <CustomDatePicker
            label="Enrollment Date"
            date={formData.enrollmentDate}
            onChange={(value) => {
            onDataChange({
              enrollmentDate: value
            });
            }}
           error={enrollmentDateError}      
          />      
        </div>
        <div className={styles.column20} data-error-field="graduationdate">
          <CustomDatePicker
            label="Expected Graduation Date"     
            date={formData.graduationDate}
            onChange={(value) => {
            onDataChange({
              graduationDate: value
            });
            }}
            error={graduationDateError}
            minDate={formData.enrollmentDate}
          />
        </div>
      </div>
      <h2>Clinical Assignment Details</h2>
      <div className={styles.row}>
        <div className={styles.column50}>
          <MultiSelectDropdown
            label="Speciality"
            options={specialityList}
            selected={formData.specialityIds}
            onChange={(selected) => {
              if(selected.length > 3){
                showToast('You can select up to 3 specialities only.', 'error')
              } else {
                onDataChange({
                  specialityIds: selected
                });
              }
            }}
          />
        </div>
        {studentId && 
        <div className={styles.column50}>   
           <MultiSelectDropdown
           label="Instructor"
           selected={formData.instructorIds}
           options={instructorList}
           disabled={true}
         />     
        </div>
          }  
      </div>
      <div className={styles.actions}>
        <Button
          label="Cancel"
          variant="white"
          size="medium"
          onClick={() => navigate('/students')}
        />
        <Button
          label="Save Student"
          variant="primary"
          size="medium"
          type="submit"
        />
      </div>
    </form>
  );
};

StudentForm.propTypes = {
  formData: PropTypes.object,
  specialityList: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      label: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired
    })
  ).isRequired,
  yearData: PropTypes.arrayOf(   
    PropTypes.shape({
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired, 
      label: PropTypes.string.isRequired
    })
  ).isRequired,
  instructorList: PropTypes.arrayOf(   
        PropTypes.shape({
          value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired, 
          label: PropTypes.string.isRequired
        })
      ).isRequired,
  onDataChange: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
}

export default StudentForm;